import { useCreateHeliosDataTablesExportMutation } from 'store/services/helios/dataHelios';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { ExportDataFromTableToFile } from 'types/dataHelios';
import { DataTableFormatType } from 'types/dataHelios';
import { FileExtension, FileMimeType } from 'types';
import { isMobileApp } from 'utils/common';
import { downloadBase64File } from 'utils/file/downloadFile';

type ExportParams = ExportDataFromTableToFile & {
  fileName: string;
};

type UseHeliosDataExportReturn = {
  exportData: (params: ExportParams) => Promise<boolean>;
  isExporting: boolean;
};

const FILE_META: Record<DataTableFormatType, { extension: FileExtension; mimeType: FileMimeType }> = {
  [DataTableFormatType.Excel]: { extension: FileExtension.Excel, mimeType: FileMimeType.Excel },
  [DataTableFormatType.Pdf]: { extension: FileExtension.Pdf, mimeType: FileMimeType.Pdf },
  [DataTableFormatType.Csv]: { extension: FileExtension.Csv, mimeType: FileMimeType.Csv },
};

const useHeliosDataExport = (): UseHeliosDataExportReturn => {
  const { t } = useTranslation();
  const mobileApp = isMobileApp();
  const [createHeliosDataTablesExport, { isLoading: isExporting }] = useCreateHeliosDataTablesExportMutation();

  const exportData = useCallback(
    async ({ fileName, exportDataTableFormatType, ...rest }: ExportParams): Promise<boolean> => {
      try {
        const result = await createHeliosDataTablesExport({
          exportDataTableFormatType,
          ...rest,
        }).unwrap();

        const { extension, mimeType } = FILE_META[exportDataTableFormatType ?? DataTableFormatType.Excel];

        downloadBase64File({ base64: result, fileName, extension, mimeType });

        if (!mobileApp) {
          toast.success(t('helios.listing.exportModal.success'));
        }

        return true;
      } catch {
        toast.error(t('common.failedToDownload'));

        return false;
      }
    },
    [createHeliosDataTablesExport, mobileApp, t]
  );

  return { exportData, isExporting };
};

export { useHeliosDataExport };
